import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect } from "react";
import "./DraggableCircle.css";
import { useTranslation } from "react-i18next";
// Toggle to false before shipping to silence all [CTRL_DEBUG] output.
const DEBUG = true;
/*const STOP_REPEAT_COUNT = 10;
const STOP_REPEAT_INTERVAL_MS = 50;*/
export const DraggableCircle = ({ controlConnection, stickPositions, isArmed, mobileActiveRef, keyboardActiveRef, }) => {
    const { t } = useTranslation();
    const bigRef = useRef(null);
    const dragOrigin = useRef(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    // Refs instead of state: applyMovement reads these inside the RAF loop and must
    // always see the current value without waiting for a re-render cycle.
    const stickX = useRef(0);
    const stickY = useRef(0);
    /*const [stickX, setStickX] = useState(0);
    const [stickY, setStickY] = useState(0);*/
    const [dragging, setDragging] = useState(false);
    // Mirrors dragging state so the RAF closure can log the current value without
    // capturing a stale closure over the dragging state variable.
    const draggingRef = useRef(false);
    const min = 0.05;
    //const stopSequenceTimers = useRef<NodeJS.Timeout[]>([]);
    const repeatAfterMs = 100;
    const isArmedRef = useRef(isArmed);
    const bigRadius = 150;
    const smallRadius = 40;
    const maxDistance = bigRadius - smallRadius;
    const stopTimer = useRef(null);
    const STOP_REPEAT_TIMES = 10; // 10 × 50ms = 500ms
    const SPOT_ENTER_THRESHOLD = 0.05; // enter spot turn when |vy| drops below this
    const SPOT_EXIT_THRESHOLD = 0.15; // exit spot turn only when |vy| rises above this
    const MIN_TURN_POWER = 0.05; // minimum forward power in the blend zone
    const isSpotMode = useRef(false);
    // Slew-rate limiter state.
    // moveCircle() writes rawVx/rawVy (the joystick target).
    // The RAF loop advances slewedVx/slewedVy toward the target, then calls applyMovement().
    const TURN_SLEW = 5.0; // units/second → full range (0→1) in 200 ms
    const POWER_SLEW = 5.0;
    const rawVx = useRef(0); // joystick vx from the most recent event
    const rawVy = useRef(0); // joystick vy from the most recent event
    const lastStickPosition = useRef([0, 0, 0, 0, 0, 1, 0]);
    const slewedVx = useRef(0); // smoothed vx currently fed to command dispatch
    const slewedVy = useRef(0); // smoothed vy currently fed to command dispatch
    const rampRaf = useRef(null);
    const rampTs = useRef(0);
    // Re-assigned on every render so the recursive RAF call always dispatches
    // the freshest closure (keeps controlConnection and applyMovement up to date).
    const rampStepFn = useRef(() => { });
    // ─── Debug instrumentation ────────────────────────────────────────────────────
    const commandId = useRef(0); // monotonically increasing per SignalR invoke
    // Last effectivePower computed by applyMovement — exposed here so dbgLog can
    // include it even from call sites that don't have the local variable in scope.
    const lastEffectivePower = useRef(0);
    /**
     * Core logging helper.
     * Always prefixes with [CTRL_DEBUG][<category>] so Chrome DevTools can filter
     * with a single search term, e.g. "[CTRL_DEBUG]" or "[CTRL_DEBUG][STOP]".
     *
     * Every entry includes the full limiter snapshot:
     *   ts, vx, vy, effectivePower, targetVx, targetPw,
     *   slewedVx, slewedPw, isSpotMode, dragging
     *
     * Callers may pass `extra` to override or extend any field.
     */
    const dbgLog = (category, msg, extra = {}) => {
        if (!DEBUG)
            return;
        console.log(`[CTRL_DEBUG][${category}] ${msg}`, Object.assign({ ts: performance.now().toFixed(2), vx: slewedVx.current.toFixed(3), vy: slewedVy.current.toFixed(3), effectivePower: lastEffectivePower.current.toFixed(3), targetVx: rawVx.current.toFixed(3), targetPw: rawVy.current.toFixed(3), slewedVx: slewedVx.current.toFixed(3), slewedPw: slewedVy.current.toFixed(3), isSpotMode: isSpotMode.current, dragging: draggingRef.current }, extra));
    };
    /*useEffect(() => {
      return () => {
        stopSequenceTimers.current.forEach(clearTimeout);
        stopSequenceTimers.current = [];
      };
    }, []);
  
    const cancelStopSequence = () => {
      stopSequenceTimers.current.forEach(clearTimeout);
      stopSequenceTimers.current = [];
    };
  
    const invokeStopRepeatedly = () => {
      cancelStopSequence();
      if (!controlConnection) return;
      for (let i = 0; i < STOP_REPEAT_COUNT; i++) {
        const timer = setTimeout(() => {
          if (controlConnection) {
            console.log("Stop");
            controlConnection.invoke("Stop");
          }
        }, i * STOP_REPEAT_INTERVAL_MS);
        stopSequenceTimers.current.push(timer);
      }
    };*/
    // ─── SignalR command wrappers ─────────────────────────────────────────────────
    // These are the ONLY functions that call controlConnection.invoke().
    // Every wrapper accepts a `source` tag so each emitted command can be traced
    // back to its exact call site.  onStop also takes a `reason` tag.
    const onGo = (value, direction) => {
        const id = ++commandId.current;
        const name = direction ? "GoForward" : "GoBackward";
        if (DEBUG) {
            dbgLog("COMMAND", `[#${id}] SEND ${name}(${value.toFixed(3)})`, {
                value,
                direction,
            });
        }
        if (controlConnection) {
            controlConnection.invoke(name, value);
        }
    };
    const onStop = () => {
        const id = ++commandId.current;
        /*if (DEBUG) {
          dbgLog("STOP", `[#${id}] SEND Stop`, { source, reason });
        }*/
        if (controlConnection) {
            controlConnection.invoke("Stop");
        }
        if (isArmed < 0 && !(keyboardActiveRef === null || keyboardActiveRef === void 0 ? void 0 : keyboardActiveRef.current)) {
            stickPositions === null || stickPositions === void 0 ? void 0 : stickPositions([0, 0, 0, 0, 0, 1, 0]);
        }
    };
    const onTurn = (direction, isSpot, turnRate, power) => {
        const id = ++commandId.current;
        //cancelStopSequence();
        console.log(`direction: ${direction}, isSpot: ${isSpot}, turnRate: ${turnRate}, power: ${power}`);
        if (direction) {
            if (isSpot) {
                if (DEBUG) {
                    dbgLog("COMMAND", `[#${id}] SEND SpotTurnRight(${turnRate.toFixed(3)})`, { isSpot, turnRate, power });
                }
                if (controlConnection) {
                    controlConnection.invoke("SpotTurnRight", turnRate);
                }
            }
            else {
                if (DEBUG) {
                    dbgLog("COMMAND", `[#${id}] SEND TurnRight(${turnRate.toFixed(3)}, ${power.toFixed(3)})`, { isSpot, turnRate, power });
                }
                if (controlConnection) {
                    controlConnection.invoke("TurnRight", turnRate, power);
                }
            }
        }
        else {
            if (isSpot) {
                if (DEBUG) {
                    dbgLog("COMMAND", `[#${id}] SEND SpotTurnLeft(${turnRate.toFixed(3)})`, { isSpot, turnRate, power });
                }
                if (controlConnection) {
                    controlConnection.invoke("SpotTurnLeft", turnRate);
                }
            }
            else {
                if (DEBUG) {
                    dbgLog("COMMAND", `[#${id}] SEND TurnLeft(${turnRate.toFixed(3)}, ${power.toFixed(3)})`, { isSpot, turnRate, power });
                }
                if (controlConnection) {
                    controlConnection.invoke("TurnLeft", turnRate, power);
                }
            }
        }
    };
    const onEndTurn = (direction, isSpot, power) => {
        if (isSpot) {
            // Spot mode has no concept of "end turn with power" — just stop.
            onStop();
            console.log("Spot stop");
            //onStop();
        }
        else {
            const id = ++commandId.current;
            const name = direction ? "EndTurnRight" : "EndTurnLeft";
            if (DEBUG) {
                dbgLog("COMMAND", `[#${id}] SEND ${name}(${power.toFixed(3)})`, {
                    direction,
                    power,
                });
            }
            if (controlConnection) {
                controlConnection.invoke(name, power);
            }
        }
    };
    // ─── Slew helpers ─────────────────────────────────────────────────────────────
    const stepToward = (curr, tgt, maxStep) => {
        const diff = tgt - curr;
        return Math.abs(diff) <= maxStep ? tgt : curr + Math.sign(diff) * maxStep;
    };
    const stopRamp = (reason = "explicit") => {
        if (rampRaf.current !== null) {
            if (DEBUG)
                dbgLog("RAMP", `stopRamp reason=${reason}`);
            cancelAnimationFrame(rampRaf.current);
            rampRaf.current = null;
        }
    };
    const startRamp = () => {
        if (rampRaf.current !== null)
            return; // already running; picks up new rawVx/rawVy on next tick
        if (DEBUG)
            dbgLog("RAMP", "startRamp");
        rampTs.current = performance.now();
        rampRaf.current = requestAnimationFrame(rampStepFn.current);
    };
    // ─── Command dispatch ─────────────────────────────────────────────────────────
    // The ONLY place SignalR commands are sent during active driving.
    // Receives already-smoothed (vx, vy) from the RAF loop.
    /*const applyMovement = (vx: number, vy: number) => {
      // Hysteresis: enter spot mode only when deeply centred, exit only when clearly off-centre
      if (isSpotMode.current) {
        if (Math.abs(vy) > SPOT_EXIT_THRESHOLD) {
          isSpotMode.current = false;
          if (DEBUG) {
            dbgLog("MODE", "Exit Spot Mode", {
              vx: vx.toFixed(3),
              vy: vy.toFixed(3),
            });
          }
        }
      } else {
        if (Math.abs(vy) < SPOT_ENTER_THRESHOLD) {
          isSpotMode.current = true;
          if (DEBUG) {
            dbgLog("MODE", "Enter Spot Mode", {
              vx: vx.toFixed(3),
              vy: vy.toFixed(3),
            });
          }
        }
      }
      const isSpot = isSpotMode.current;
  
      // In the blend zone [SPOT_ENTER, SPOT_EXIT], smoothly reduce forward power instead of
      // switching immediately to SpotTurn.  Outside the blend zone effectivePower == vy.
      let effectivePower = vy;
      if (!isSpot && Math.abs(vy) < SPOT_EXIT_THRESHOLD) {
        const t =
          (Math.abs(vy) - SPOT_ENTER_THRESHOLD) /
          (SPOT_EXIT_THRESHOLD - SPOT_ENTER_THRESHOLD);
        effectivePower =
          Math.sign(vy) * (MIN_TURN_POWER + t * (Math.abs(vy) - MIN_TURN_POWER));
      }
      lastEffectivePower.current = effectivePower;
  
      if (
        Math.abs(Math.abs(vx) - Math.abs(stickX.current)) > min ||
        Math.abs(Math.abs(vy) - Math.abs(stickY.current)) > min
      ) {
        if (Math.abs(vx) <= min) {
          if (Math.abs(stickX.current) > min) {
            onEndTurn(
              stickX.current > 0,
              isSpot,
              isSpot ? Math.abs(vx) : Math.abs(effectivePower),
              "rampStep",
            );
          } else {
            if (Math.abs(vy) > min) {
              onGo(Math.abs(vy), vy > 0, "rampStep");
            } else {
              onStop("rampStep", "NoTurnAndNoPower");
            }
          }
        } else {
          onTurn(vx > 0, isSpot, Math.abs(vx), effectivePower, "rampStep");
        }
  
        if (Math.abs(Math.abs(vx) - Math.abs(stickX.current)) > min) {
          if (DEBUG) {
            dbgLog(
              "STATE",
              `stickX ${stickX.current.toFixed(3)} → ${vx.toFixed(3)}`,
              {
                vx: vx.toFixed(3),
                vy: vy.toFixed(3),
                effectivePower: effectivePower.toFixed(3),
              },
            );
          }
          stickX.current = vx;
        }
        if (Math.abs(Math.abs(vy) - Math.abs(stickY.current)) > min) {
          if (DEBUG) {
            dbgLog(
              "STATE",
              `stickY ${stickY.current.toFixed(3)} → ${vy.toFixed(3)}`,
              {
                vx: vx.toFixed(3),
                vy: vy.toFixed(3),
                effectivePower: effectivePower.toFixed(3),
              },
            );
          }
          stickY.current = vy;
        }
      }
    };*/
    const applyMovement = (vx, vy) => {
        // ─────────────────────────────────────────────
        // 1. Spot mode hysteresis
        // ─────────────────────────────────────────────
        if (isSpotMode.current) {
            if (Math.abs(vy) > SPOT_EXIT_THRESHOLD) {
                isSpotMode.current = false;
                if (DEBUG) {
                    dbgLog("MODE", "Exit Spot Mode", {
                        vx: vx.toFixed(3),
                        vy: vy.toFixed(3),
                    });
                }
            }
        }
        else {
            if (Math.abs(vy) < SPOT_ENTER_THRESHOLD) {
                isSpotMode.current = true;
                if (DEBUG) {
                    dbgLog("MODE", "Enter Spot Mode", {
                        vx: vx.toFixed(3),
                        vy: vy.toFixed(3),
                    });
                }
            }
        }
        const isSpot = isSpotMode.current;
        // ─────────────────────────────────────────────
        // 2. Calculate effective power
        // ─────────────────────────────────────────────
        let effectivePower = vy;
        if (!isSpot && Math.abs(vy) < SPOT_EXIT_THRESHOLD) {
            const t = (Math.abs(vy) - SPOT_ENTER_THRESHOLD) /
                (SPOT_EXIT_THRESHOLD - SPOT_ENTER_THRESHOLD);
            effectivePower =
                Math.sign(vy) * (MIN_TURN_POWER + t * (Math.abs(vy) - MIN_TURN_POWER));
        }
        lastEffectivePower.current = effectivePower;
        // ─────────────────────────────────────────────
        // 3. ALWAYS send current movement command
        //
        // The joystick is still held, so the command
        // must continue to be sent even if the position
        // did not change.
        // ─────────────────────────────────────────────
        if (Math.abs(vx) <= min) {
            if (Math.abs(vy) > min) {
                onGo(Math.abs(vy), vy > 0);
            }
            else {
                //onStop("rampStep", "NoTurnAndNoPower");
            }
        }
        else {
            onTurn(vx > 0, isSpot, Math.abs(vx), effectivePower);
        }
        // ─────────────────────────────────────────────
        // 4. Update remembered stick position
        //
        // This is ONLY state tracking.
        // It must NOT control whether the command
        // is sent or not.
        // ─────────────────────────────────────────────
        const xChanged = Math.abs(vx - stickX.current) > min;
        const yChanged = Math.abs(vy - stickY.current) > min;
        if (xChanged) {
            if (DEBUG) {
                dbgLog("STATE", `stickX ${stickX.current.toFixed(3)} → ${vx.toFixed(3)}`, {
                    vx: vx.toFixed(3),
                    vy: vy.toFixed(3),
                    effectivePower: effectivePower.toFixed(3),
                });
            }
            stickX.current = vx;
        }
        if (yChanged) {
            if (DEBUG) {
                dbgLog("STATE", `stickY ${stickY.current.toFixed(3)} → ${vy.toFixed(3)}`, {
                    vx: vx.toFixed(3),
                    vy: vy.toFixed(3),
                    effectivePower: effectivePower.toFixed(3),
                });
            }
            stickY.current = vy;
        }
    };
    // Assigned on every render so the loop always has a fresh applyMovement closure.
    rampStepFn.current = (ts) => {
        const dt = Math.min((ts - rampTs.current) / 1000, 0.1); // cap at 100 ms (e.g. tab switch)
        rampTs.current = ts;
        slewedVx.current = stepToward(slewedVx.current, rawVx.current, TURN_SLEW * dt);
        slewedVy.current = stepToward(slewedVy.current, rawVy.current, POWER_SLEW * dt);
        if (DEBUG) {
            dbgLog("RAMP", `tick dt=${dt.toFixed(4)}s`, {
                dt,
                vx: slewedVx.current.toFixed(3),
                vy: slewedVy.current.toFixed(3),
            });
        }
        applyMovement(slewedVx.current, slewedVy.current);
        /*const atTarget =
          slewedVx.current === rawVx.current && slewedVy.current === rawVy.current;
        if (atTarget) {
          if (DEBUG) dbgLog("RAMP", "ramp completed");
          rampRaf.current = null;
        } else {
          rampRaf.current = requestAnimationFrame(rampStepFn.current);
        }*/
        if (draggingRef.current) {
            if (isArmed < 0 && !(keyboardActiveRef === null || keyboardActiveRef === void 0 ? void 0 : keyboardActiveRef.current)) {
                stickPositions === null || stickPositions === void 0 ? void 0 : stickPositions(lastStickPosition.current);
            }
            rampRaf.current = requestAnimationFrame(rampStepFn.current);
        }
        else {
            rampRaf.current = null;
        }
    };
    // ─── Input handling ───────────────────────────────────────────────────────────
    // moveCircle: translates screen coordinates into normalised (vx, vy), updates the
    // visual puck position and the raw joystick targets.  Does NOT send any commands.
    const moveCircle = (x, y) => {
        if (!bigRef.current || !dragOrigin.current)
            return;
        let dx = x - dragOrigin.current.x;
        let dy = y - dragOrigin.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > maxDistance) {
            dx = (maxDistance * dx) / distance;
            dy = (maxDistance * dy) / distance;
        }
        setOffset({ x: dx, y: dy });
        const vx = dx / maxDistance;
        const vy = -(dy / maxDistance);
        const isSpot = Math.abs(vy) <= min;
        if (Math.abs(Math.abs(vx) - Math.abs(stickX.current)) > min ||
            Math.abs(Math.abs(vy) - Math.abs(stickY.current)) > min) {
            if (Math.abs(vx) <= min) {
                if (Math.abs(stickX.current) > min) {
                    onEndTurn(stickX.current > 0, isSpot, isSpot ? Math.abs(vx) : Math.abs(vy));
                }
                else {
                    if (Math.abs(vy) > min) {
                        onGo(Math.abs(vy), vy > 0);
                    }
                    else {
                        //onStop();
                    }
                }
            }
            else {
                onTurn(vx > 0, isSpot, Math.abs(vx), vy);
            }
            lastStickPosition.current = [0, 0, vy, vx, 0, 1, 0];
            if (isArmed < 0 && !(keyboardActiveRef === null || keyboardActiveRef === void 0 ? void 0 : keyboardActiveRef.current)) {
                stickPositions === null || stickPositions === void 0 ? void 0 : stickPositions(lastStickPosition.current);
            }
            if (DEBUG) {
                dbgLog("INPUT", `moveCircle raw=(${x.toFixed(0)},${y.toFixed(0)}) → vx=${vx.toFixed(3)}, vy=${vy.toFixed(3)}`, { vx: vx.toFixed(3), vy: vy.toFixed(3) });
            }
            if (Math.abs(Math.abs(vx) - Math.abs(stickX.current)) > min) {
                stickX.current = vx;
            }
            if (Math.abs(Math.abs(vy) - Math.abs(stickY.current)) > min) {
                stickY.current = vy;
            }
            rawVx.current = vx;
            rawVy.current = vy;
            startRamp();
            console.log({ x: vx, y: vy });
        }
    };
    const startDrag = (e) => {
        if (DEBUG)
            dbgLog("INPUT", "startDrag");
        if (stopTimer.current) {
            clearInterval(stopTimer.current);
            stopTimer.current = null;
        }
        //cancelStopSequence();
        if ("touches" in e) {
            dragOrigin.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
        else {
            dragOrigin.current = { x: e.clientX, y: e.clientY };
        }
        draggingRef.current = true;
        setDragging(true);
        if (mobileActiveRef)
            mobileActiveRef.current = true;
    };
    useEffect(() => {
        isArmedRef.current = isArmed;
    }, [isArmed]);
    useEffect(() => {
        const stopTimer = setInterval(() => {
            if (!dragging && controlConnection && isArmedRef.current === -1) {
                console.log("Stop1111");
                controlConnection.invoke("Stop");
            }
        }, repeatAfterMs);
        return () => {
            clearInterval(stopTimer);
        };
    }, [dragging, controlConnection]);
    const onDrag = (e) => {
        if (!draggingRef.current)
            return;
        if (e.type === "touchmove") {
            const touch = e.touches[0];
            moveCircle(touch.clientX, touch.clientY);
        }
        else {
            const mouse = e;
            moveCircle(mouse.clientX, mouse.clientY);
        }
    };
    const endDrag = () => {
        if (DEBUG)
            dbgLog("INPUT", "endDrag");
        dragOrigin.current = null;
        draggingRef.current = false;
        setDragging(false);
        if (mobileActiveRef)
            mobileActiveRef.current = false;
        setOffset({ x: 0, y: 0 });
        // Cancel the RAF first so it cannot emit any command after this point.
        stopRamp("endDrag");
        rawVx.current = 0;
        rawVy.current = 0;
        slewedVx.current = 0;
        slewedVy.current = 0;
        stickX.current = 0;
        stickY.current = 0;
        isSpotMode.current = false;
        lastEffectivePower.current = 0;
        if (DEBUG)
            dbgLog("STATE", "endDrag — all state zeroed");
        if (stopTimer.current) {
            clearInterval(stopTimer.current);
            stopTimer.current = null;
        }
        onStop();
        let count = 0;
        const burstId = setInterval(() => {
            onStop();
            count++;
            if (count >= STOP_REPEAT_TIMES - 1) {
                clearInterval(burstId);
                stopTimer.current = null;
                if (DEBUG)
                    dbgLog("STOP", "stop-repeat burst completed");
            }
        }, 50);
        stopTimer.current = burstId;
        stickX.current = 0;
        stickY.current = 0;
        onStop();
    };
    useEffect(() => {
        return () => {
            if (stopTimer.current) {
                clearInterval(stopTimer.current);
                stopTimer.current = null;
            }
            stopRamp("unmount");
        };
    }, []);
    useEffect(() => {
        document.addEventListener("mousemove", onDrag);
        document.addEventListener("mouseup", endDrag);
        document.addEventListener("touchmove", onDrag);
        document.addEventListener("touchend", endDrag);
        document.addEventListener("touchcancel", endDrag);
        return () => {
            document.removeEventListener("mousemove", onDrag);
            document.removeEventListener("mouseup", endDrag);
            document.removeEventListener("touchmove", onDrag);
            document.removeEventListener("touchend", endDrag);
            document.removeEventListener("touchcancel", endDrag);
        };
    });
    return (_jsx("div", { className: `touch-control ${!Boolean(isArmed) && "dis-armed"}`, children: _jsxs("div", { ref: bigRef, className: "big-circle", children: [!Boolean(isArmed) && (_jsx("div", { className: "dis-armed-info", children: t("draggableCircle.armed") })), _jsx("div", { className: `small-circle ${dragging ? "dragging" : ""}`, style: {
                        transform: `translate(${offset.x}px, ${offset.y}px)`,
                    }, onMouseDown: startDrag, onTouchStart: startDrag })] }) }));
};
